import type { CompareLevel } from "./compare.ts";

/**
 * The comparison's configuration as the URL carries it.
 *
 * Everything the reader can set on the compare screen lives in the query string,
 * so a reload, a bookmark or a pasted link lands on the same picture. Defaults
 * are written as absent keys rather than values, which keeps a fresh URL clean
 * and lets the automatic choices keep moving with the data until the reader
 * pins one down.
 */

/** The query keys, all optional. */
export type CompareParams = {
  view?: string;
  a?: string;
  b?: string;
  level?: string;
  axes?: string;
  open?: string;
  persona?: string;
};

export type CompareSelection = {
  /** The view to evaluate; empty reads the newest report instead. */
  view: string;
  /** The left product, or null for the automatic opening pair. */
  a: string | null;
  /** The right product, or null for the automatic opening pair. */
  b: string | null;
  level: CompareLevel;
  /** The axes the reader picked, or null while the biggest differences decide. */
  axes: string[] | null;
  /**
   * The difference rows folded open. Empty is the resting state rather than an
   * automatic one, so this needs no null: nothing opens until a reader asks.
   */
  open: string[];
  /**
   * The persona whose weighting the reader asked to see, or null for whatever
   * the page decides on its own: a customer's own persona, nothing for a consultant.
   */
  persona: string | null;
};

/** Stands for "no axes at all" in the URL, since an empty value would drop the key. */
export const noAxesMarker = "-";

/*
 * Properties are the resting level: that is where the questions, and with them
 * the reasoning behind a score, are one fold away. Topics stay one click up.
 */
const levelOf = (raw: string | undefined): CompareLevel =>
  raw === "topic" ? "topic" : "capability";

const axesOf = (raw: string | undefined): string[] | null => {
  if (raw === undefined || raw === "") return null;
  if (raw === noAxesMarker) return [];
  return raw.split(",").filter((axis) => axis !== "");
};

const openOf = (raw: string | undefined): string[] =>
  raw === undefined || raw === "" ? [] : raw.split(",").filter((id) => id !== "");

/** The selection the query string describes, with defaults where it is silent. */
export const readSelection = (params: CompareParams): CompareSelection => ({
  view: params.view ?? "",
  a: params.a === undefined || params.a === "" ? null : params.a,
  b: params.b === undefined || params.b === "" ? null : params.b,
  level: levelOf(params.level),
  axes: axesOf(params.axes),
  open: openOf(params.open),
  persona: params.persona === undefined || params.persona === "" ? null : params.persona,
});

/**
 * The query string for a selection. Defaults come back as empty strings, which
 * the router turns into a removed key.
 */
export const writeSelection = (selection: CompareSelection): Required<CompareParams> => ({
  view: selection.view,
  a: selection.a ?? "",
  b: selection.b ?? "",
  level: selection.level === "capability" ? "" : selection.level,
  axes:
    selection.axes === null
      ? ""
      : selection.axes.length === 0
        ? noAxesMarker
        : selection.axes.join(","),
  open: selection.open.join(","),
  persona: selection.persona ?? "",
});
